import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { formatTime } from "../../utils/dateUtils";

import Popover from "@material-ui/core/Popover";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";

import TicketListItem from "../TicketListItem";
import useTickets from "../../hooks/useTickets";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { resolveContactAvatar } from "../../utils/avatar";
import toastError from "../../errors/toastError";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import useToast from "../../hooks/useToast";
import defaultLogoFavicon from "../../assets/favicon.ico";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import IconButtonWithTooltip from "../ui/IconButtonWithTooltip";
import { Bell } from "lucide-react";
import soundService from "../../services/soundService";
import useMutedGroups from "../../hooks/useMutedGroups";
import notificationPollingService from "../../services/notificationPollingService";
import pushNotificationService from "../../services/pushNotificationService";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  tabContainer: {
    overflowY: "auto",
    maxHeight: 380,
    ...theme.scrollbarStyles,
    padding: theme.spacing(1),
  },
  popoverPaper: {
    width: "100%",
    maxWidth: 380,
    marginTop: theme.spacing(1),
    borderRadius: 16,
    overflow: "hidden",
    // ✅ Sombra elegante e performática: suave no light, mais pronunciada no dark
    boxShadow: theme.mode === "dark"
      ? "0 8px 32px rgba(0,0,0,0.4)"
      : "0 4px 16px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      maxWidth: 320,
      borderRadius: 12,
    },
  },
  popoverHeader: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: "#fff",
    padding: theme.spacing(0.875, 1.25),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  headerIcon: {
    width: 24,
    height: 24,
    borderRadius: 5,
    backgroundColor: "rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "#fff",
    lineHeight: 1.2,
  },
  headerSubtitle: {
    fontSize: "0.65rem",
    color: "rgba(255,255,255,0.75)",
    marginTop: 0,
  },
  emptyList: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  emptyIcon: {
    width: 48,
    height: 48,
    margin: "0 auto 12px",
    opacity: 0.4,
    color: theme.palette.text.disabled,
  },
  emptyText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  notificationContainer: {
    width: "100%",
    backgroundColor: theme.palette.background.paper,
  },
  notificationTicket: {
    width: "100%",
    cursor: "pointer",
    borderRadius: 10,
    marginBottom: theme.spacing(0.5),
    transition: "all 0.2s ease",
    overflow: "hidden",
    backgroundColor: theme.palette.background.default,
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}10`,
    },
    "&:last-child": {
      marginBottom: 0,
    },
  },
  ticketItemContainer: {
    borderRadius: 10,
    overflow: "hidden",
    "& .MuiListItem-root": {
      border: "none !important",
      backgroundColor: "transparent !important",
    },
    "& .MuiListItemButton-root": {
      border: "none !important",
      backgroundColor: "transparent !important",
    },
  },
  // 🎯 Badge customizado
  notificationBadge: {
    "& .MuiBadge-badge": {
      fontSize: "0.65rem",
      height: "18px",
      minWidth: "18px",
      padding: "0 5px",
      fontWeight: 700,
      backgroundColor: theme.palette.error.main,
      color: "#fff",
      boxShadow: `0 2px 8px ${theme.palette.error.main}50`,
    },
  },
}));

const NotificationsPopOver = React.memo(({ volume }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { profile, queues } = user;

  const ticketIdUrl = +history.location.pathname.split("/")[2];
  const ticketIdRef = useRef(ticketIdUrl);
  const anchorEl = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unviewedAnnouncementsCount, setUnviewedAnnouncementsCount] = useState(0);
  const unviewedAnnouncementIdsRef = useRef(new Set());
  const [announcementPreviews, setAnnouncementPreviews] = useState([]);

  const { get: getSetting } = useCompanySettings();
  const { setTabOpen } = useContext(TicketsContext);
  const toast = useToast();
  
  // 🔇 Hook para gerenciar grupos silenciados
  const { isGroupMuted } = useMutedGroups();
  const isGroupMutedRef = useRef(isGroupMuted);
  
  // Manter ref atualizada para uso em callbacks
  useEffect(() => {
    isGroupMutedRef.current = isGroupMuted;
  }, [isGroupMuted]);

  const [showTicketWithoutQueue, setShowTicketWithoutQueue] = useState(false);
  const [showNotificationPending, setShowNotificationPending] = useState(true);
  const [showGroupNotification, setShowGroupNotification] = useState(false);
  
  // ✅ Estado para controlar permissão de notificações
  const [notificationPermission, setNotificationPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );

  const [desktopNotifications, setDesktopNotifications] = useState([]);

  // 🔒 FIX: Calcular queueIds do usuário para filtrar no backend
  // Notificações devem respeitar APENAS as filas do atendente (inclusive admin/supervisor)
  const userQueueIds = useMemo(
    () => (Array.isArray(queues) ? queues.map((q) => q.id) : []),
    [queues]
  );

  // ✅ Inicializar serviço de polling (fallback quando socket desconecta)
  // Este serviço garante notificações mesmo sem Socket.IO (como WhatsApp Web)
  useEffect(() => {
    if (user?.companyId && userQueueIds.length >= 0) {
      notificationPollingService.initialize(user.companyId, userQueueIds);
    }
    
    // Cleanup não necessário - serviço é singleton
  }, [user?.companyId, userQueueIds]);

  const fetchUnviewedAnnouncements = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements/unviewed");
      setUnviewedAnnouncementsCount(data.count || 0);
      unviewedAnnouncementIdsRef.current = new Set(data.announcementIds || []);
    } catch (err) {
      console.error("Erro ao buscar informativos não vistos:", err);
    }
  }, []);

  const fetchAnnouncementPreviews = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements", {
        params: { pageNumber: 1, scope: "all" }
      });
      setAnnouncementPreviews(data.records || []);
    } catch (err) {
      console.error("Erro ao buscar informativos:", err);
      setAnnouncementPreviews([]);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchUnviewedAnnouncements();
      fetchAnnouncementPreviews();
    }
  }, [isOpen, fetchUnviewedAnnouncements, fetchAnnouncementPreviews]);

  useEffect(() => {
    fetchUnviewedAnnouncements();
    fetchAnnouncementPreviews();
  }, [fetchUnviewedAnnouncements, fetchAnnouncementPreviews]);

  const handleAnnouncementClick = useCallback(async (announcementId) => {
    if (!announcementId) return;
    const unviewedIds = unviewedAnnouncementIdsRef.current;
    if (unviewedIds.has(announcementId)) {
      unviewedIds.delete(announcementId);
      setUnviewedAnnouncementsCount((c) => Math.max(0, c - 1));
      try {
        await api.post(`/announcements/${announcementId}/view`);
      } catch (err) {
        unviewedIds.add(announcementId);
        setUnviewedAnnouncementsCount((c) => c + 1);
      }
    }
    history.push("/announcements", { announcementId });
  }, [history]);

  const { tickets } = useTickets({
    status: "pending",
    // 🔒 FIX: Passar filas do usuário para filtrar no backend
    queueIds: JSON.stringify(userQueueIds),
    // 🔒 FIX: showAll=false garante que backend filtra por filas
    showAll: false,
  });

  const normalizedVolume = useMemo(() => {
    const vol = typeof volume === "number" ? volume : Number(volume) || 1;
    return Math.max(0, Math.min(1, vol));
  }, [volume]);

  const soundAlertRef = useRef();
  const handleNotificationsRef = useRef(null);
  const showDesktopNotificationRef = useRef(null);
  const notificationsRef = useRef([]);
  // ✅ Rastrear tickets que já tocaram som para evitar duplicatas
  const ticketsThatPlayedSoundRef = useRef(new Set());

  // 🔊 Usar o novo serviço de som robusto
  const soundAlertGroupRef = useRef(); // Ref para som de grupos
  const soundAlertStarredRef = useRef(); // ⭐ Ref para som de favoritos
  
  // ⭐ CRÍTICO: Manter lista local de tickets favoritos do usuário
  // O webhook do backend não sabe calcular isStarred pessoal, sempre envia false
  // Então mantemos nossa própria lista atualizada via:
  // 1. Evento "starToggle" do Socket.IO
  // 2. Evento customizado "starred-tickets-loaded" do TicketsListCustom
  const starredTicketsRef = useRef(new Set());
  
  // ⭐ Escutar evento customizado para popular lista de favoritos quando tickets carregam
  useEffect(() => {
    const handleStarredTicketsLoaded = (event) => {
      const { starredTicketIds } = event.detail || {};
      if (Array.isArray(starredTicketIds)) {
        starredTicketIds.forEach(id => starredTicketsRef.current.add(id));
      }
    };
    
    window.addEventListener("starred-tickets-loaded", handleStarredTicketsLoaded);
    
    return () => {
      window.removeEventListener("starred-tickets-loaded", handleStarredTicketsLoaded);
    };
  }, []);
  
  useEffect(() => {
    soundService.setVolume(normalizedVolume);
    soundAlertRef.current = () => soundService.play();
    soundAlertGroupRef.current = () => soundService.playGroup(); // Som de grupos
    soundAlertStarredRef.current = () => soundService.playStarred(); // ⭐ Som de favoritos
  }, [normalizedVolume]);

  const historyRef = useRef(history);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    const unlisten = history.listen((location) => {
      const currentId = +String(location.pathname || "").split("/")[2];
      ticketIdRef.current = currentId;

      if (currentId && location.pathname.startsWith("/tickets/")) {
        pushNotificationService.closeNotification(String(currentId));
        pushNotificationService.closeNotification(`ticket-${currentId}`);
        setDesktopNotifications((prev) =>
          prev.filter((n) => n.tag !== String(currentId) && n.tag !== `ticket-${currentId}`)
        );
      }
    });
    return () => {
      if (typeof unlisten === "function") unlisten();
    };
  }, [history]);

  // ✅ Solicitar permissão via pushNotificationService (centralizado) e auto-subscribe push
  useEffect(() => {
    const unsubPermission = pushNotificationService.onPermissionChange((perm) => {
      setNotificationPermission(perm);
    });

    pushNotificationService.requestPermission().then((perm) => {
      setNotificationPermission(perm);
      if (perm === "granted") {
        pushNotificationService.subscribe();
      }
    });

    // Listener para NOTIFICATION_CLICK do Service Worker
    const handleSWMessage = (event) => {
      if (event.data?.type === "NOTIFICATION_CLICK") {
        const { ticketUuid, ticketId, ticketStatus } = event.data;
        if (ticketStatus && setTabOpen) {
          setTabOpen(ticketStatus);
        }
        
        let route = null;
        if (ticketUuid && ticketUuid !== "undefined") {
          route = `/tickets/${ticketUuid}`;
        } else if (ticketId && ticketId !== "undefined") {
          route = `/tickets/${ticketId}`;
        }
        
        if (route) {
          historyRef.current.push(route);
        }
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", handleSWMessage);
    }

    return () => {
      unsubPermission();
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", handleSWMessage);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setTabOpen]);

  useEffect(() => {
    if (!Array.isArray(tickets)) return;

    // 🔒 FIX: Notificações devem SEMPRE respeitar filas do usuário
    // Admin/supervisor NÃO tem bypass aqui - só vê notificações das suas filas
    const filteredTickets = tickets.filter((ticket) => {
      // Ticket sem fila: só mostra se showTicketWithoutQueue (allTicket enabled)
      if (!ticket.queueId) {
        if (!showTicketWithoutQueue) return false;
      } else {
        // Ticket com fila: só mostra se atendente pertence à fila
        if (!userQueueIds.includes(ticket.queueId)) return false;
      }

      if (ticket.isGroup) {
        return (
          showGroupNotification &&
          ticket.whatsapp?.groupAsTicket === "enabled" &&
          ticket.status === "pending"
        );
      }
      return ticket.status === "pending";
    });

    setNotifications(filteredTickets);
    notificationsRef.current = filteredTickets; // ✅ Manter referência atualizada
  }, [tickets, showGroupNotification, userQueueIds, showTicketWithoutQueue]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const setting = await getSetting({
          column: "showNotificationPending",
        });

        if (setting.showNotificationPending === true) {
          setShowNotificationPending(true);
        }

        if (user.allTicket === "enabled") {
          setShowTicketWithoutQueue(true);
        }
        if (user.allowGroup === true) {
          setShowGroupNotification(true);
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.allTicket, user?.allowGroup, user?.companyId]);

  // ✅ Função de fallback: exibe toast quando desktop notification não está disponível
  const showNotificationToast = useCallback((data) => {
    const { contact, message } = data;
    const messagePreview = message.body?.substring(0, 50) || "Nova mensagem";
    toast.info(
      `${contact.name}: ${messagePreview}${message.body?.length > 50 ? "..." : ""}`,
      { autoClose: 5000 }
    );
  }, [toast]);

  // 🔔 Notificação via Service Worker — funciona com aba em background/throttled
  const showDesktopNotification = useCallback(
    (data) => {
      const { message, contact, ticket } = data;

      const permission = pushNotificationService.getPermission();
      if (permission !== "granted") {
        showNotificationToast(data);
        return;
      }

      const title = `${i18n.t("tickets.notification.message")} ${contact.name}`;
      const tag = String(ticket.id);
      const options = {
        body: `${message.body} - ${formatTime(new Date())}`,
        icon: resolveContactAvatar(contact),
        tag,
        renotify: true,
        data: {
          url: ticket.uuid && ticket.uuid !== "undefined"
            ? `/tickets/${ticket.uuid}`
            : `/tickets/${ticket.id}`,
          ticketId: ticket.id,
          ticketUuid: ticket.uuid,
          ticketStatus: ticket.status,
        },
      };

      pushNotificationService.showNotification(title, options).then((shown) => {
        if (!shown) {
          showNotificationToast(data);
        }
      });

      setDesktopNotifications((prevState) => {
        const idx = prevState.findIndex((n) => n.tag === tag);
        const entry = { tag, ticketId: ticket.id };
        if (idx !== -1) {
          const updated = [...prevState];
          updated[idx] = entry;
          return updated;
        }
        return [entry, ...prevState];
      });
    },
    [showNotificationToast]
  );

  // handleNotifications mantido para retrocompatibilidade (chama showDesktopNotification + som)
  const handleNotifications = useCallback(
    (data) => {
      showDesktopNotification(data);

      // Tocar som independente do tipo de notificação
      const { ticket } = data;
      const isGroup = ticket?.isGroup || false;
      const isMuted = isGroup && isGroupMuted(ticket?.id);
      
      // ⭐ Favoritos são PESSOAIS — confiar apenas na lista local (starredTicketsRef)
      // O ticket.isStarred do backend é legado/global e não deve ser usado para som
      const isStarred = ticket?.id ? starredTicketsRef.current.has(ticket.id) : false;
      
      if (!isMuted) {
        if (isStarred && soundAlertStarredRef.current) {
          soundAlertStarredRef.current();
        } else if (isGroup && soundAlertGroupRef.current) {
          soundAlertGroupRef.current();
        } else if (soundAlertRef.current) {
          soundAlertRef.current();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showDesktopNotification, normalizedVolume, isGroupMuted]
  );

  useEffect(() => {
    handleNotificationsRef.current = handleNotifications;
  }, [handleNotifications]);

  useEffect(() => {
    showDesktopNotificationRef.current = showDesktopNotification;
  }, [showDesktopNotification]);

  useEffect(() => {
    const companyId = user.companyId;
    // ✅ FIX: Verificar se socket existe antes de usar
    if (user.id && socket) {
      const onConnectNotificationsPopover = () => {
        socket.emit("joinNotification");
      };

      // 🔒 FIX: Notificações devem SEMPRE respeitar filas do usuário
      // Admin/supervisor NÃO tem bypass - só recebe notificações das suas filas
      const socketUserQueueIds = user?.queues?.map((q) => q.id) || [];


      // ✅ Função auxiliar para tocar som de notificação
      // ⭐ Prioridade de som: Favorito > Grupo > Individual
      const playNotificationSound = (ticketId, reason = "unknown", isGroup = false, _isStarredLegacy = false, ticketTab = null) => {
        // 🔇 Verificar se é grupo silenciado ANTES de qualquer coisa
        if (isGroup && isGroupMutedRef.current && isGroupMutedRef.current(ticketId)) {
          return false;
        }

        // Evitar tocar som duplicado para o mesmo ticket
        if (ticketsThatPlayedSoundRef.current.has(ticketId)) {
          return false;
        }

        // Marcar como tocado
        ticketsThatPlayedSoundRef.current.add(ticketId);
        
        // Limpar após 5 segundos para permitir novo som se necessário
        setTimeout(() => {
          ticketsThatPlayedSoundRef.current.delete(ticketId);
        }, 5000);

        // ⭐ Favoritos são PESSOAIS — confiar apenas na lista local (starredTicketsRef)
        const isStarred = starredTicketsRef.current.has(ticketId);
        
        // ⭐ PRIORIDADE: Som de favorito tem prioridade máxima (ex: chefe chamando)
        // 📁 TICKET TAB: Abas com useStarredSound=true também tocam som de favorito
        if ((isStarred || ticketTab?.useStarredSound) && soundAlertStarredRef.current) {
          soundAlertStarredRef.current();
          return true;
        }
        
        // 🔊 Tocar som - usar som diferente para grupos
        if (isGroup && soundAlertGroupRef.current) {
          soundAlertGroupRef.current();
          return true;
        } else if (soundAlertRef.current) {
          soundAlertRef.current();
          return true;
        }
        return false;
      };

      const onCompanyTicketNotificationsPopover = (data) => {
        // ⭐ Atualizar lista de tickets favoritos quando receber evento starToggle
        if (data.action === "starToggle" && data.ticketId && data.userId === user?.id) {
          if (data.isStarred) {
            starredTicketsRef.current.add(data.ticketId);
          } else {
            starredTicketsRef.current.delete(data.ticketId);
          }
            return;
        }
        
        if (data.action === "update" && data.ticket) {
          if (data.ticket?.status !== "pending") {
            // Remover da lista de notificações
            setNotifications((prevState) => {
              const filtered = prevState.filter((t) => t.id !== data.ticket?.id);
              notificationsRef.current = filtered;
              return filtered;
            });

            const closingTag = String(data.ticket?.id);
            pushNotificationService.closeNotification(closingTag);
            setDesktopNotifications((prevState) => {
              return prevState.filter((n) => n.tag !== closingTag);
            });
          } else {
            const ticket = data.ticket;
            const hasQueueAccess = 
              (!ticket.queueId && showTicketWithoutQueue) ||
              (ticket.queueId && socketUserQueueIds.includes(ticket.queueId));

            if (!hasQueueAccess) {
              return;
            }

            setNotifications((prevState) => {
              const ticketIndex = prevState.findIndex(
                (t) => t.id === data.ticket?.id
              );
              const isNewTicket = ticketIndex === -1;
              
              if (ticketIndex !== -1) {
                const updatedTickets = [...prevState];
                updatedTickets[ticketIndex] = data.ticket;
                notificationsRef.current = updatedTickets;
                return updatedTickets;
              }
              
              // ✅ CORREÇÃO: Tocar som quando um ticket NOVO é adicionado à lista
              // Isso garante que a primeira mensagem toque o som, mesmo se o evento
              // de ticket chegar antes do evento de mensagem
              if (isNewTicket && 
                  ticket.status === "pending" && 
                  (!ticket.userId || ticket.userId === user?.id) &&
                  showNotificationPending === true) {
                // Tocar som para ticket novo na fila (grupos silenciados são tratados em playNotificationSound)
                // ⭐ Passar isStarred para tocar som especial de favoritos
                setTimeout(() => {
                  playNotificationSound(ticket.id, "ticket-event-new-pending", ticket.isGroup, ticket.isStarred, ticket.ticketTab);
                }, 50);
              }
              
              const newState = [...prevState, data.ticket];
              notificationsRef.current = newState;
              return newState;
            });
          }
        }

        if (data.action === "delete") {
          setNotifications((prevState) => {
            const ticketIndex = prevState.findIndex(
              (t) => t.id === data.ticketId
            );
            if (ticketIndex !== -1) {
              const updated = [...prevState];
              updated.splice(ticketIndex, 1);
              notificationsRef.current = updated; // ✅ Atualizar referência
              return updated;
            }
            return prevState;
          });

          const deletingTag = String(data.ticketId);
          pushNotificationService.closeNotification(deletingTag);
          setDesktopNotifications((prevState) => {
            return prevState.filter((n) => n.tag !== deletingTag);
          });
        }
      };

      const onCompanyAppMessageNotificationsPopover = (data) => {
        // 🔒 FIX: Obter IDs das filas e whatsapps do usuário para verificar acesso
        // Notificações respeitam APENAS filas do usuário (sem bypass de admin)
        const msgUserQueueIds = user?.queues?.map((q) => q.id) || [];
        const userWhatsappIds = user?.queues?.flatMap((q) => q.whatsapps?.map(w => w.id) || []) || [];
        
        // 🔒 FIX: Função auxiliar para verificar se o usuário tem acesso ao ticket
        // Admin/supervisor NÃO tem bypass - só recebe notificações das suas filas
        const userHasAccessToTicket = (ticket) => {
          if (!ticket) return false;
          // Verificar se está na fila direta
          if (msgUserQueueIds.includes(ticket?.queueId)) {
            return true;
          }
          // Verificar se é ticket sem fila e usuário tem permissão (allTicket)
          if (!ticket?.queueId && showTicketWithoutQueue) {
            return true;
          }
          // 🎯 GRUPOS COMPARTILHADOS: Verificar sharedWhatsappIds
          if (ticket?.isGroup && ticket?.sharedWhatsappIds?.length > 0) {
            const ticketWhatsappIds = [ticket?.whatsappId, ...(ticket?.sharedWhatsappIds || [])];
            return ticketWhatsappIds.some(wId => userWhatsappIds.includes(wId));
          }
          return false;
        };

        // ✅ CONDIÇÃO BASE: Só processar mensagens novas que não são do atendente
        if (data.action !== "create" || data.message?.fromMe) {
          return;
        }

        // ✅ Verificar se é o ticket atualmente visível
        const isSameVisibleTicket =
          data.message?.ticketId === ticketIdRef.current &&
          document.visibilityState === "visible";

        // ✅ Se estiver vendo o ticket, NÃO tocar som
        if (isSameVisibleTicket) {
          return;
        }

        // ✅ Ignorar tickets com status lgpd/nps
        if (["lgpd", "nps"].includes(data.ticket?.status)) {
          return;
        }

        const ticketId = data.ticket?.id;
        if (!ticketId) return;
        
        const isGroup = data.ticket?.isGroup || false;

        // 🎯 GRUPOS: Tratamento especial ANTES da verificação de acesso genérica
        // Grupos NÃO são tickets - têm sua própria lógica de permissão (allowGroup)
        // Não passam pela verificação de filas/queueId
        if (isGroup) {
          // Verificar se usuário tem permissão para receber notificações de grupos
          if (!showGroupNotification) {
            return;
          }
          
          // 🔊 GRUPOS: Tocar som se não estiver silenciado
          // ⭐ Grupos também podem ser favoritos!
          if (!ticketsThatPlayedSoundRef.current.has(ticketId)) {
            playNotificationSound(ticketId, "appMessage-group", true, data.ticket?.isStarred, data.ticket?.ticketTab);
          }
          return; // Grupos não entram na lógica de tickets pendentes
        }

        // ✅ Verificar acesso ao ticket (apenas para tickets INDIVIDUAIS, não grupos)
        if (!userHasAccessToTicket(data.ticket)) {
          return;
        }

        // ✅ PENDING: Tickets pendentes (aguardando atendimento) - apenas tickets individuais
        if (
          data.ticket?.status === "pending" &&
          (data.ticket?.userId === user?.id || !data.ticket?.userId) &&
          showNotificationPending === true
        ) {
          // ✅ Verificar se é ticket novo ANTES de atualizar estado
          const ticketAlreadyInList = notificationsRef.current.some(
            (t) => t.id === ticketId
          );
          const isNewTicket = !ticketAlreadyInList;
          
          setNotifications((prevState) => {
            const ticketIndex = prevState.findIndex(
              (t) => t.id === ticketId
            );
            if (ticketIndex !== -1) {
              const updatedTickets = [...prevState];
              updatedTickets[ticketIndex] = data.ticket;
              notificationsRef.current = updatedTickets;
              return updatedTickets;
            }
            const newState = [data.ticket, ...prevState];
            notificationsRef.current = newState;
            return newState;
          });

          const shouldNotNotificate =
            (data.ticket?.userId && data.ticket?.userId !== user?.id);

          // ✅ MELHORIA 8.2: Separar desktop notification de som para evitar duplicação
          // ANTES: handleNotifications tocava som + playNotificationSound tocava de novo (duplo)
          // AGORA: showDesktopNotification (visual) + playNotificationSound (som) separados
          if (shouldNotNotificate !== true) {
            if (showDesktopNotificationRef.current) {
              showDesktopNotificationRef.current(data);
            }
            // Tocar som UMA única vez (não mais dentro do handleNotifications)
            if (isNewTicket || !ticketsThatPlayedSoundRef.current.has(ticketId)) {
              playNotificationSound(ticketId, "appMessage-pending", false, data.ticket?.isStarred, data.ticket?.ticketTab);
            }
          }
          return;
        }

        // ✅ OPEN: Tickets abertos do próprio atendente
        if (
          data.ticket?.status === "open" &&
          data.ticket?.userId === user?.id
        ) {
          if (!ticketsThatPlayedSoundRef.current.has(ticketId)) {
            playNotificationSound(ticketId, "appMessage-open", false, data.ticket?.isStarred, data.ticket?.ticketTab);
          }
          return;
        }

        // 🔔 EQUIPES: Notificar colegas de equipe do dono do ticket
        if (
          data.ticket?.status === "open" &&
          data.ticket?.userId !== user?.id &&
          Array.isArray(data.ticket?.teamMemberUserIds) &&
          data.ticket.teamMemberUserIds.includes(user?.id)
        ) {
          if (!ticketsThatPlayedSoundRef.current.has(ticketId)) {
            playNotificationSound(ticketId, "appMessage-open-team", false, data.ticket?.isStarred, data.ticket?.ticketTab);
          }
          // Mostrar desktop notification para o colega de equipe
          if (showDesktopNotificationRef.current) {
            showDesktopNotificationRef.current(data);
          }
          return;
        }

        // ✅ Tickets sem userId (na fila) - tocar som se atendente tem acesso
        if (!data.ticket?.userId) {
          if (!ticketsThatPlayedSoundRef.current.has(ticketId)) {
            playNotificationSound(ticketId, "appMessage-no-userId", false, data.ticket?.isStarred, data.ticket?.ticketTab);
          }
        }
      };

      const onUserSystemNotification = (data) => {
        const message = data?.message || "Notificação";
        const type = data?.type || "info";
        const toastOptions = { autoClose: 5000 };

        if (toast[type]) {
          toast[type](message, toastOptions);
        } else {
          toast.info(message, toastOptions);
        }

        if (data?.sound !== false) {
          soundService.play();
        }
      };

      // 🔔 Notificação dedicada de transferência de ticket para o novo atendente.
      // O backend emite este evento direcionado SOMENTE para user-${newUserId},
      // mas validamos novamente aqui (defesa em profundidade) e respeitamos a
      // preferência do usuário (notifyOnTransferReceived).
      const onCompanyTicketActionNotification = (data) => {
        if (!data) return;
        // Ambos os eventos (transferência direta e entrada em fila) são
        // direcionados a user-${id}; revalidamos aqui (defesa em profundidade).
        if (data.toUserId !== user.id) return;

        // 🔄 Transferência DIRETA para este atendente.
        if (data.action === "transferred") {
          if (user?.notifyOnTransferReceived === false) return;

          try {
            soundService.play();
          } catch {
            // som silenciado, segue só o desktop notification
          }

          if (showDesktopNotificationRef.current) {
            const fromName = data.fromUserName || "Outro atendente";
            const contactName = data.contactName || "Cliente";
            const queueName = data.queueName ? ` · ${data.queueName}` : "";
            showDesktopNotificationRef.current({
              ticket: {
                id: data.ticketId,
                uuid: data.ticketUuid,
                isGroup: false
              },
              contact: { name: `${contactName}${queueName}` },
              message: { body: `Transferido por ${fromName}` }
            });
          } else {
            toast.info(
              `Ticket de ${data.contactName || "cliente"} transferido por ${data.fromUserName || "outro atendente"}`,
              { autoClose: 6000 }
            );
          }
          return;
        }

        // 🔔 Entrada em FILA: o backend emite "queued" para cada membro da fila.
        if (data.action === "queued") {
          try {
            soundService.play();
          } catch {
            // som silenciado, segue só o desktop notification
          }

          const contactName = data.contactName || "Cliente";
          const queueName = data.queueName ? ` · ${data.queueName}` : "";
          if (showDesktopNotificationRef.current) {
            showDesktopNotificationRef.current({
              ticket: {
                id: data.ticketId,
                uuid: data.ticketUuid,
                isGroup: false
              },
              contact: { name: `${contactName}${queueName}` },
              message: { body: `Novo atendimento na fila` }
            });
          } else {
            toast.info(
              `Novo atendimento na fila${queueName}: ${contactName}`,
              { autoClose: 6000 }
            );
          }
        }
      };

      const isAnnouncementRelevant = (record) => {
        if (!record?.status) return false;
        if (record.scope === "private") {
          return record.userId === user?.id;
        }
        return true;
      };

      const handleAnnouncement = (data) => {
        if (!data?.record) return;
        const announcementId = data.record.id;
        const unviewedIds = unviewedAnnouncementIdsRef.current;

        if (data.action === "create" || data.action === "update") {
          if (!isAnnouncementRelevant(data.record)) {
            if (unviewedIds.has(announcementId)) {
              unviewedIds.delete(announcementId);
              setUnviewedAnnouncementsCount((c) => Math.max(0, c - 1));
            }
            return;
          }
          if (!unviewedIds.has(announcementId)) {
            unviewedIds.add(announcementId);
            setUnviewedAnnouncementsCount((c) => c + 1);
          }
        }

        if (data.action === "delete") {
          if (unviewedIds.has(announcementId)) {
            unviewedIds.delete(announcementId);
            setUnviewedAnnouncementsCount((c) => Math.max(0, c - 1));
          }
        }
      };

      if (socket.connected) {
        onConnectNotificationsPopover();
      }
      socket.on("connect", onConnectNotificationsPopover);
      socket.on(
        `company-${companyId}-ticket`,
        onCompanyTicketNotificationsPopover
      );
      socket.on(
        `company-${companyId}-appMessage`,
        onCompanyAppMessageNotificationsPopover
      );
      socket.on(
        `user-${user.id}-notification`,
        onUserSystemNotification
      );
      socket.on(
        `company-${companyId}-ticket-action`,
        onCompanyTicketActionNotification
      );
      socket.on(
        "company-announcement",
        handleAnnouncement
      );
      socket.on(
        `user-${user.id}-announcement`,
        handleAnnouncement
      );

      return () => {
        socket.off("connect", onConnectNotificationsPopover);
        socket.off(
          `company-${companyId}-ticket`,
          onCompanyTicketNotificationsPopover
        );
        socket.off(
          `company-${companyId}-appMessage`,
          onCompanyAppMessageNotificationsPopover
        );
        socket.off(
          `user-${user.id}-notification`,
          onUserSystemNotification
        );
        socket.off(
          `company-${companyId}-ticket-action`,
          onCompanyTicketActionNotification
        );
        socket.off(
          "company-announcement",
          handleAnnouncement
        );
        socket.off(
          `user-${user.id}-announcement`,
          handleAnnouncement
        );
      };
    }
  }, [
    user,
    profile,
    queues,
    showTicketWithoutQueue,
    socket,
    showNotificationPending,
    showGroupNotification,
    toast,
  ]);

  const handleClick = useCallback(() => {
    setIsOpen((prevState) => !prevState);
  }, []);

  const handleClickAway = useCallback(() => {
    if (!modalOpen) {
      setIsOpen(false);
    }
  }, [modalOpen]);

  const handleNotificationClick = useCallback(
    (ticket) => {
      setIsOpen(false);

      setTimeout(() => {
        if (setTabOpen) {
          setTabOpen(ticket.status);
        }
      }, 10);
    },
    [setTabOpen]
  );

  const NotificationTicket = useCallback(
    ({ children, ticket }) => {
      return (
        <div
          className={classes.notificationTicket}
          onClick={() => handleNotificationClick(ticket)}
        >
          <div className={classes.ticketItemContainer}>{children}</div>
        </div>
      );
    },
    [
      handleNotificationClick,
      classes.notificationTicket,
      classes.ticketItemContainer,
    ]
  );

  const faviconCanvasRef = useRef(null);
  const originalFaviconRef = useRef(null);

  const browserNotification = useMemo(() => {
    const alertCount = notifications.length + unviewedAnnouncementsCount;
    const numbers = "⓿➊➋➌➍➎➏➐➑➒➓⓫⓬⓭⓮⓯⓰⓱⓲⓳⓴";
    if (alertCount > 0) {
      if (alertCount < 21) {
        document.title =
          numbers.substring(alertCount, alertCount + 1) +
          " - " +
          (theme.appName || "...");
      } else {
        document.title =
          "(" + alertCount + ")" + (theme.appName || "...");
      }
    } else {
      document.title = theme.appName || "...";
    }

    const faviconUrl = theme?.appLogoFavicon || defaultLogoFavicon;

    const updateFavicon = () => {
      const linkEl = document.querySelector("link[rel~='icon']") || document.createElement("link");
      linkEl.rel = "icon";

      if (alertCount === 0) {
        linkEl.href = faviconUrl;
        if (!document.querySelector("link[rel~='icon']")) document.head.appendChild(linkEl);
        return;
      }

      const canvas = faviconCanvasRef.current || document.createElement("canvas");
      faviconCanvasRef.current = canvas;
      canvas.width = 64;
      canvas.height = 64;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        linkEl.href = faviconUrl;
        if (!document.querySelector("link[rel~='icon']")) document.head.appendChild(linkEl);
        return;
      }

      const img = originalFaviconRef.current || new Image();
      originalFaviconRef.current = img;
      img.crossOrigin = "anonymous";
      img.onload = () => {
        ctx.clearRect(0, 0, 64, 64);
        ctx.drawImage(img, 0, 0, 64, 64);

        const badgeText = alertCount > 99 ? "99+" : String(alertCount);
        const badgeWidth = badgeText.length > 2 ? 40 : 28;

        ctx.beginPath();
        ctx.arc(64 - badgeWidth / 2, badgeWidth / 2, badgeWidth / 2 + 2, 0, 2 * Math.PI);
        ctx.fillStyle = "#ef4444";
        ctx.fill();

        ctx.font = "bold 24px Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#ffffff";
        ctx.fillText(badgeText, 64 - badgeWidth / 2, badgeWidth / 2 + 1);

        linkEl.href = canvas.toDataURL("image/png");
        if (!document.querySelector("link[rel~='icon']")) document.head.appendChild(linkEl);
      };
      img.onerror = () => {
        linkEl.href = faviconUrl;
        if (!document.querySelector("link[rel~='icon']")) document.head.appendChild(linkEl);
      };
      if (img.src !== faviconUrl) {
        img.src = faviconUrl;
      } else {
        img.onload();
      }
    };

    try {
      updateFavicon();
    } catch (e) {
      // Fallback silencioso
    }

    return null;
  }, [notifications.length, unviewedAnnouncementsCount, theme.appName, theme.appLogoFavicon]);

  const badgeProps = useMemo(
    () => {
      // ✅ Usar notifications.length (já filtrado por fila do atendente)
      // Nota: limitado a 40 tickets pela API, mas respeita o filtro de filas
      const count = notifications.length + unviewedAnnouncementsCount;
      return {
        color: "secondary",
        badgeContent: count > 99 ? "99+" : count,
        invisible: count === 0,
        max: 99,
        className: classes.notificationBadge,
      };
    },
    [notifications.length, unviewedAnnouncementsCount, classes.notificationBadge]
  );

  const renderNotifications = useMemo(() => {
    const unviewedIds = unviewedAnnouncementIdsRef.current;
    const unviewedAnnouncements = announcementPreviews.filter((a) => unviewedIds.has(a.id));

    if (notifications.length === 0) {
      if (unviewedAnnouncements.length === 0) {
        return (
          <div className={classes.emptyList}>
            <Bell className={classes.emptyIcon} />
            <Typography className={classes.emptyText}>
              {i18n.t("notifications.noTickets") ||
                "Nenhuma notificação no momento"}
            </Typography>
          </div>
        );
      }
    }

    return (
      <>
        {unviewedAnnouncements.length > 0 && (
          <Box mb={1} px={1}>
            <Typography variant="caption" color="textSecondary">
              Informativos não vistos
            </Typography>
            <List dense>
              {unviewedAnnouncements.slice(0, 5).map((announcement) => (
                <ListItem
                  key={announcement.id}
                  button
                  onClick={() => handleAnnouncementClick(announcement.id)}
                >
                  <ListItemText
                    primary={announcement.title || "Informativo"}
                    secondary={announcement.scope === "private" ? "Privado" : "Global"}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {notifications.map((ticket) => (
          <NotificationTicket key={ticket.id} ticket={ticket}>
            <TicketListItem
              ticket={ticket}
              setTabOpen={setTabOpen}
              setModalOpen={setModalOpen}
              hideLastMessage
            />
          </NotificationTicket>
        ))}
      </>
    );
  }, [notifications, announcementPreviews, classes.emptyList, classes.emptyIcon, classes.emptyText, setTabOpen, setModalOpen, handleAnnouncementClick]);

  return (
    <>
      {browserNotification}

      <div ref={anchorEl}>
        <IconButtonWithTooltip
          icon={Bell}
          tooltip={
            notificationPermission === "denied"
              ? "Notificações bloqueadas pelo navegador"
              : `${i18n.t("notifications.title")}${
                  desktopNotifications.length > 0
                    ? ` (${desktopNotifications.length})`
                    : ""
                }`
          }
          onClick={handleClick}
          aria-label={i18n.t("notifications.title")}
          badgeProps={badgeProps}
          showWarning={notificationPermission === "denied"}
        />
      </div>
      <Popover
        disableScrollLock
        open={isOpen}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{ paper: classes.popoverPaper }}
        onClose={handleClickAway}
      >
        <div className={classes.popoverHeader}>
          <div className={classes.headerIcon}>
            <Bell size={14} color="#fff" />
          </div>
          <div className={classes.headerContent}>
            <Typography className={classes.headerTitle}>
              {i18n.t("notifications.title") || "Notificações"}
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {notifications.length + unviewedAnnouncementsCount > 0
                ? `${notifications.length + unviewedAnnouncementsCount} ${
                    notifications.length + unviewedAnnouncementsCount === 1
                      ? "pendente"
                      : "pendentes"
                  }`
                : "Tudo em dia!"}
            </Typography>
          </div>
        </div>
        <div className={classes.notificationContainer}>
          <div className={classes.tabContainer}>
            {renderNotifications}
          </div>
        </div>
      </Popover>
    </>
  );
});

export default NotificationsPopOver;
